import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';

const useQuestionBankStore = create(
  persist(
    (set, get) => ({
      questions: [],
      loading: false,
      error: null,
      currentPage: 1,
      rowsPerPage: 10,
      searchTerm: '',
      searchId: '',
      month: '',
      year: '',
      availableMonths: [],
      availableYears: [],
      monthYearsLoading: false,

      setSearchTerm: (term) => set({ searchTerm: term }),
      setSearchId: (id) => set({ searchId: id }),
      setMonth: (month) => set({ month }),
      setYear: (year) => set({ year }),

      fetchMonthYears: async (filters = {}) => {
        try {
          set({ monthYearsLoading: true });
          const {
            courseCode = '',
            sylCode = '',
            subCode = '',
            unitCode = '',
            moduleCode = '',
            topicCode = '',
            month = '',
            year = '',
          } = filters || {};

          const response = await apiClient.get(API_ENDPOINTS.questionBankMonthYears, {
            params: {
              courseCode,
              sylCode,
              subCode,
              unitCode,
              moduleCode,
              topicCode,
              month,
              year,
            },
          });
          const payload = response.data?.data ?? response.data;
          let months = [];
          let years = [];

          if (payload && typeof payload === 'object') {
            if (Array.isArray(payload)) {
              const mSet = new Set();
              const ySet = new Set();
              payload.forEach((item) => {
                if (typeof item === 'object' && item !== null) {
                  if (item.year !== undefined && item.year !== null && item.year !== '') {
                    ySet.add(String(item.year));
                  }
                  if (Array.isArray(item.years)) {
                    item.years.forEach((y) => y !== undefined && y !== null && y !== '' && ySet.add(String(y)));
                  }

                  const itemYearStr = item.year !== undefined && item.year !== null ? String(item.year) : '';
                  const filterYearStr = year !== undefined && year !== null ? String(year) : '';
                  const matchesYear = !filterYearStr || !itemYearStr || itemYearStr === filterYearStr;

                  if (matchesYear) {
                    if (typeof item.month === 'string' && item.month) {
                      mSet.add(item.month);
                    } else if (Array.isArray(item.month)) {
                      item.month.forEach((m) => m && mSet.add(String(m)));
                    }

                    if (Array.isArray(item.months)) {
                      item.months.forEach((m) => {
                        if (m !== undefined && m !== null && m !== '') {
                          mSet.add(String(m));
                        }
                      });
                    } else if (typeof item.months === 'string' && item.months) {
                      mSet.add(item.months);
                    }
                  }
                } else if (typeof item === 'string' || typeof item === 'number') {
                  ySet.add(String(item));
                }
              });

              months = Array.from(mSet);
              years = Array.from(ySet);
            } else {
              if (Array.isArray(payload.months)) {
                months = payload.months.map(String);
              }
              if (Array.isArray(payload.years)) {
                years = payload.years.map(String);
              }
            }
          }

          // Sort years in descending order (recent years first)
          years.sort((a, b) => {
            const valA = typeof a === 'object' && a !== null ? (a.year ?? a.value) : a;
            const valB = typeof b === 'object' && b !== null ? (b.year ?? b.value) : b;
            const numA = Number(valA);
            const numB = Number(valB);
            if (!isNaN(numA) && !isNaN(numB)) {
              return numB - numA;
            }
            return String(valB).localeCompare(String(valA));
          });

          // Sort months chronologically
          const MONTH_ORDER = [
            'jan', 'feb', 'mar', 'apr', 'may', 'jun',
            'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
          ];
          const getMonthRank = (m) => {
            const str = typeof m === 'object' && m !== null ? (m.month ?? m.value ?? m.label ?? '') : String(m);
            const lowerStr = str.trim().toLowerCase().slice(0, 3);
            const idx = MONTH_ORDER.indexOf(lowerStr);
            return idx !== -1 ? idx : 99;
          };
          months.sort((a, b) => getMonthRank(a) - getMonthRank(b));

          set({ availableMonths: months, availableYears: years, monthYearsLoading: false });
        } catch (error) {
          console.error("Failed to fetch month-years from questionbank", error);
          set({ availableMonths: [], availableYears: [], monthYearsLoading: false });
        }
      },

      searchQuestions: async (filters = {}) => {
        try {
          set({ loading: true, error: null });
          const {
            keyword = '',
            courseCode = '',
            sylCode = '',
            subCode = '',
            unitCode = '',
            moduleCode = '',
            topicCode = '',
            month = '',
            year = '',
          } = filters || {};

          const response = await apiClient.get(API_ENDPOINTS.searchQuestions, {
            params: {
              courseCode,
              sylCode,
              subCode,
              unitCode,
              moduleCode,
              topicCode,
              month,
              year,
              keyword,
            },
          });
          const payload = response.data;
          const data = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
          set({ questions: data, loading: false });
        } catch (error) {
          console.error("Failed to search questions in bank", error);
          set({ questions: [], error: 'Failed to search questions.', loading: false });
        }
      },

      setCurrentPage: (page) => {
        set({ currentPage: page });
      },

      resetStore: () => {
        set({
          questions: [],
          loading: false,
          error: null,
          currentPage: 1,
          searchTerm: '',
          searchId: '',
          month: '',
          year: '',
        });
        get().searchQuestions({});
      }
    }),
    {
      name: 'question-bank-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        currentPage: state.currentPage,
        searchTerm: state.searchTerm,
        searchId: state.searchId,
        month: state.month,
        year: state.year,
      }),
    }
  )
);

export default useQuestionBankStore;
